import type { Private } from '@seek/indie-api-types';
import {
  Actions,
  Alert,
  Button,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';
import { useOrganizations } from '#src/pages/manage/organizations/OrganizationContext.tsx';

import { RoleSelector } from '#src/pages/manage/users/components/RoleSelector.tsx';
import { type Role, roleDisplayNames } from '#src/types/users.ts';

interface EditRoleProps {
  user: Private.Auth0UserPayload;
  onSave?: () => void;
}

export const EditRole = ({ user, onSave }: EditRoleProps) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<Role>(user.role);
  const [error, setError] = useState(null);
  const showToast = useToast();

  const organizations = useOrganizations();

  const save = async () => {
    if (value === user.role) {
      showToast({
        tone: 'positive',
        message: `User already has this role`,
      });
      return;
    }

    setLoading(true);

    try {
      await organizations.setNewRole({ id: user.id, role: value });
      showToast({
        tone: 'positive',
        message: `Successfully updated role`,
        description: `${user.name} to ${roleDisplayNames[value]}`,
      });
      setError(null);
      onSave?.();
    } catch {
      setError(error);
    }

    setLoading(false);
  };

  return (
    <Stack space="medium">
      <RoleSelector value={value} onChange={(newValue) => setValue(newValue)} />
      <Actions>
        <Button type="submit" loading={loading} onClick={save}>
          {loading ? 'Saving' : 'Save'}
        </Button>
      </Actions>
      {error ? (
        <Alert tone="critical">
          <Stack space="small">
            <Text>There was a problem updating {user.name} role.</Text>
            <Text size="small">{error}</Text>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
};
